'use client';

import Icon from '@/components/shared/Icons';
import type { EventType } from '@/utils/filters/options/getEventTypeOptions';
import { useApplyFilter } from '@/hooks/useApplyFilter';
import useAnalytics from '@/hooks/useAnalytics';
import { getSimpleClickFact } from '@/analytics/helpers/cventAnalytics/getSimpleClickFact';
import { SimpleClickFact } from '@/analytics/models/cventAnalytics/SimpleClickFact';
import { useCallback } from 'react';
import { Link } from '@/lib/router-events/patch-router/link';
import type { Filter, MeasurementUnit } from '@cvent/venue-search-shared/types';
import { decodeFilterString } from '@/actions/decodeFilterString';
import { queryStringConstants } from '@/constants/queryConstants';
import useMap from '@/hooks/useMap';
import { Categories } from '@/constants/csnSearchStatsConstants';

type Props = {
  options: EventType[];
  strings: {
    header: string;
    restaurant: string;
    budget: string;
    conference: string;
    party: string;
    corporate: string;
    large: string;
    outdoor: string;
    hotel: string;
    small: string;
  };
  searchTerm: string;
  measurementUnit: MeasurementUnit;
};

async function getFiltersForCategory(category: string, measurementUnit: MeasurementUnit): Promise<Filter | undefined> {
  const hubPage = Array.from(Categories.values()).find(f => f.path === category);
  if (hubPage) {
    if (measurementUnit === queryStringConstants.IMPERIAL) return decodeFilterString(hubPage.imperialFilter);
    return decodeFilterString(hubPage.metricFilter);
  }
  return undefined;
}

export default function EventTypeAndVenueTypeList({ options, strings, searchTerm, measurementUnit }: Props) {
  const { applyHubPagesNavigation } = useApplyFilter();
  const analytics = useAnalytics();
  const { mapView } = useMap();

  const triggerSimpleClickFact = useCallback(
    async category => {
      const filters = await getFiltersForCategory(category, measurementUnit);
      const simpleClickFact = getSimpleClickFact(
        'SEARCH_2.0',
        category,
        'null',
        'null',
        mapView,
        searchTerm,
        'null',
        'null',
        JSON.stringify(filters)
      );
      await analytics.reportFactImmediately<SimpleClickFact>(simpleClickFact);
    },
    [analytics, mapView, measurementUnit, searchTerm]
  );

  return (
    <div>
      <div className="leading-4 mb-2 text-b-md font-medium text-neutral-60">{strings.header}</div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {options.map(option => (
          <Link
            type="link"
            aria-label={option.label}
            key={option.value}
            className="cursor-pointer rounded-xl border border-neutral-30 bg-fill hover:bg-interactive-10"
            href={applyHubPagesNavigation(option.path)}
            onClick={() => triggerSimpleClickFact(option.path)}
          >
            <div className="flex-center mx-4 my-2 flex h-11 items-center">
              <Icon id={option.iconName} className="float-left mr-2 h-6 w-6" />
              <div className="flex-center items-center">
                <div className="leading-4 text-b-md font-medium text-neutral-60">{option.label}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
